import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, Observable, catchError, filter, switchMap, take, throwError } from 'rxjs';

import { APIEndpointMethods } from '@constant/api-endpoint-methods';
import { HttpStatusCodes } from '@constant/http-status-code-constant';
import { AuthService } from '@services/auth.service';
import { ApiResponse } from '@models/class/api-response';

interface IRefreshTokenResponse {
  token?: string;
  refreshToken?: string;
}

@Injectable()
export class TokenRefreshInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private readonly refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
  ) {}

  /*
   * Silently refreshes the JWT when a 401 response is received from any
   * endpoint other than the refresh-token endpoint itself. Uses a BehaviorSubject
   * as a queue so that concurrent in-flight requests wait for the single refresh
   * call to complete, then replay with the new token. On refresh failure the
   * storage is cleared and the user is navigated to home after a brief delay
   * to allow any pending animations to finish.
   */
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError(error => {
        if (
          error instanceof HttpErrorResponse &&
          error.status === HttpStatusCodes.UNAUTHORIZED_CODE &&
          !req.url.includes(APIEndpointMethods.refreshToken)
        ) {
          if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            const auths = this.authService.getUserSession();

            if (auths) {
              const accessToken = auths?.token;
              const refreshToken = auths?.refreshToken;

              return this.authService.refreshAccessToken(accessToken, refreshToken).pipe(
                switchMap((response: ApiResponse<IRefreshTokenResponse>) => {
                  const responseData = response.result;
                  const newAuths = auths;
                  newAuths.token = responseData?.token;
                  newAuths.refreshToken = responseData?.refreshToken;
                  this.authService.setUserSession(newAuths);

                  this.refreshTokenSubject.next(responseData?.token);

                  const clonedRequest = req.clone({
                    setHeaders: {
                      Authorization: `Bearer ${responseData?.token}`,
                    },
                  });

                  this.isRefreshing = false;
                  return next.handle(clonedRequest);
                }),
                catchError(err => {
                  this.isRefreshing = false;
                  this.authService.clearStorage();
                  setTimeout(() => {
                    this.navigateToLogin();
                  }, 100);
                  return throwError(() => err);
                }),
              );
            } else {
              this.isRefreshing = false;
              return throwError(() => error);
            }
          } else {
            return this.refreshTokenSubject.pipe(
              filter(token => token != null),
              take(1),
              switchMap(token => {
                const clonedRequest = req.clone({
                  setHeaders: {
                    Authorization: `Bearer ${token}`,
                  },
                });
                return next.handle(clonedRequest);
              }),
            );
          }
        }
        return throwError(() => error);
      }),
    );
  }
  /*
   * Triggers a global auth-refresh signal so listening components can react
   * (e.g. clear user state), then redirects to home with a cache-busting
   * timestamp query param to force a fresh page load.
   */
  private navigateToLogin(): void {
    this.authService.triggerRefresh();
    this.router.navigate(['/'], { queryParams: { refresh: new Date().getTime() } });
  }
}
